'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// ─── Sovereign Identity Gate ────────────────────────────────────────────────
// α = 1/137.035999084 — fine-structure constant used as identity seed
// Only one sovereign identity is permitted. No exceptions. No backdoors.
const SOVEREIGN_IDENTITY = 'yigitmiko2734@gmail.com';

function isSovereignUser(userObj: any): boolean {
  if (!userObj) return false;
  const email: string = (userObj.email || '').toLowerCase().trim();
  return email === SOVEREIGN_IDENTITY.toLowerCase();
}
// ────────────────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Whether this user has completed biometric enrollment
  const [biometricEnrolled, setBiometricEnrolled] = useState(false);
  const supabase = createClient();

  // Check if the authenticated user has a biometric_owner record
  const checkBiometricEnrollment = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('biometric_owner')
        .select('id, is_active, face_enrolled, voice_enrolled')
        .eq('user_id', userId)
        .eq('is_active', true)
        .single();
      if (error || !data) return false;
      return data.face_enrolled && data.voice_enrolled;
    } catch {
      return false;
    }
  };

  // Enroll the sovereign owner — called once after first successful biometric scan
  const enrollBiometricOwner = async (userId: string, email: string): Promise<void> => {
    const { error } = await supabase.from('biometric_owner').upsert(
      {
        user_id: userId,
        owner_email: email,
        owner_name: 'Mikail Çiloğlu',
        face_enrolled: true,
        voice_enrolled: true,
        is_active: true,
        alpha_signature: '1/137.035999084',
      },
      { onConflict: 'id' }
    );
    if (!error) setBiometricEnrolled(true);
  };

  // Log an intrusion attempt
  const logIntrusionAttempt = async (email: string, reason: string): Promise<void> => {
    try {
      await supabase.from('biometric_intrusion_log').insert({
        attempted_email: email,
        blocked_reason: reason,
      });
    } catch {
      // Silent — never let logging block the gate
    }
  };

  // Expel any non-sovereign session immediately
  const expelUnauthorized = async (userObj: any) => {
    const email = userObj?.email || 'unknown';
    await logIntrusionAttempt(
      email,
      'Non-sovereign identity — α=1/137 gate engaged. Access permanently denied.'
    );
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setBiometricEnrolled(false);
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        if (!isSovereignUser(session.user)) {
          await expelUnauthorized(session.user);
        } else {
          setSession(session);
          setUser(session.user);
          const enrolled = await checkBiometricEnrollment(session.user.id);
          setBiometricEnrolled(enrolled);
        }
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (session?.user) {
        if (!isSovereignUser(session.user)) {
          await expelUnauthorized(session.user);
        } else {
          setSession(session);
          setUser(session.user);
          const enrolled = await checkBiometricEnrollment(session.user.id);
          setBiometricEnrolled(enrolled);
        }
      } else {
        setSession(null);
        setUser(null);
        setBiometricEnrolled(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Email/Password Sign Up
  const signUp = async (email: string, password: string, metadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: (metadata as any)?.fullName || '',
          avatar_url: (metadata as any)?.avatarUrl || '',
        },
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => user?.email_confirmed_at !== null;

  // Get User Profile from Database
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  const value = {
    user,
    session,
    loading,
    biometricEnrolled,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    enrollBiometricOwner,
    checkBiometricEnrollment,
    logIntrusionAttempt,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
